import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Avatar,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Flex,
  useColorModeValue,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Divider,
  SimpleGrid,
  Tooltip
} from '@chakra-ui/react';
import {
  MdPerson,
  MdEmail,
  MdAssignment,
  MdCheckCircle,
  MdPending,
  MdAccessTime,
  MdTrendingUp,
  MdSwapHoriz,
  MdVisibility
} from 'react-icons/md';
import { FaBalanceScale } from 'react-icons/fa';
import api from '../../../../services/api';

const TeamOverview = () => {
  const [teamData, setTeamData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [newJuridicoId, setNewJuridicoId] = useState('');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isReassignOpen, onOpen: onReassignOpen, onClose: onReassignClose } = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchTeamOverview();
  }, []);

  const fetchTeamOverview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/juridico/team/overview');
      console.log('Team Overview Response:', response);
      const data = response.data || response || {};
      setTeamData(data.data || data || {});
    } catch (error) {
      console.error('Error fetching team overview:', error);
      toast({
        title: 'Erro ao carregar equipe',
        description: 'Não foi possível carregar os dados da equipe',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (memberId) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/juridico/team/member/${memberId}`);
      console.log('Member Details Response:', response);
      const data = response.data || response || {};
      setMemberDetails(data.data || data || {});
    } catch (error) {
      console.error('Error fetching member details:', error);
      toast({
        title: 'Erro ao carregar detalhes',
        description: 'Não foi possível carregar os detalhes do membro',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewMember = async (member) => {
    setSelectedMember(member);
    onOpen();
    await fetchMemberDetails(member.member._id);
  };

  const handleReassign = (assignment) => {
    setSelectedAssignment(assignment);
    onReassignOpen();
  };

  const submitReassignment = async () => {
    if (!newJuridicoId) {
      toast({
        title: 'Selecione um membro',
        description: 'Por favor, selecione o novo responsável',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setReassigning(true);
      await api.post(`/juridico/team/reassign/${selectedAssignment._id}`, {
        newJuridicoId
      });

      toast({
        title: 'Sucesso',
        description: 'Precatório reatribuído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onReassignClose();
      fetchTeamOverview();
      if (selectedMember) {
        fetchMemberDetails(selectedMember.member._id);
      }
    } catch (error) {
      console.error('Error reassigning:', error);
      toast({
        title: 'Erro ao reatribuir',
        description: 'Não foi possível reatribuir o precatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReassigning(false);
      setNewJuridicoId('');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      pendente: 'gray',
      em_analise: 'blue',
      aprovado: 'green',
      reprovado: 'red'
    };
    return colors[status] || 'gray';
  };

  const MemberCard = ({ member }) => (
    <Card
      bg={cardBg}
      shadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
    >
      <CardBody>
        <VStack align="stretch" spacing={4}>
          {/* Member Header */}
          <HStack spacing={3}>
            <Avatar
              size="md"
              name={member.member.name}
              bg="purple.500"
            />
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg">
                {member.member.name}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                {member.member.email}
              </Text>
            </Box>
          </HStack>

          <Divider />

          {/* Statistics Grid */}
          <SimpleGrid columns={2} spacing={3}>
            <Box>
              <Text fontSize="xs" color={mutedColor}>Total Designado</Text>
              <Text fontSize="xl" fontWeight="bold">
                {member.stats.totalAssigned}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color={mutedColor}>Taxa Conclusão</Text>
              <HStack>
                <Text fontSize="xl" fontWeight="bold">
                  {member.stats.completionRate}%
                </Text>
                <Icon as={MdTrendingUp} color="green.500" />
              </HStack>
            </Box>
          </SimpleGrid>

          {/* Progress Bars */}
          <VStack align="stretch" spacing={2}>
            <Box>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs">Concluídas</Text>
                <Text fontSize="xs">{member.stats.completed}</Text>
              </Flex>
              <Progress
                value={(member.stats.completed / member.stats.totalAssigned) * 100 || 0}
                colorScheme="green"
                size="sm"
                borderRadius="full"
              />
            </Box>
            <Box>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs">Em Análise</Text>
                <Text fontSize="xs">{member.stats.inAnalysis}</Text>
              </Flex>
              <Progress
                value={(member.stats.inAnalysis / member.stats.totalAssigned) * 100 || 0}
                colorScheme="blue"
                size="sm"
                borderRadius="full"
              />
            </Box>
            <Box>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs">Pendentes</Text>
                <Text fontSize="xs">{member.stats.pending}</Text>
              </Flex>
              <Progress
                value={(member.stats.pending / member.stats.totalAssigned) * 100 || 0}
                colorScheme="orange"
                size="sm"
                borderRadius="full"
              />
            </Box>
          </VStack>

          {/* Document Stats */}
          <Box p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              Análise de Documentos
            </Text>
            <HStack justify="space-between">
              <VStack spacing={0}>
                <Text fontSize="xs" color={mutedColor}>Aprovados</Text>
                <Text fontSize="sm" fontWeight="bold" color="green.500">
                  {member.stats.approvedDocuments}
                </Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="xs" color={mutedColor}>Reprovados</Text>
                <Text fontSize="sm" fontWeight="bold" color="red.500">
                  {member.stats.reprovedDocuments}
                </Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="xs" color={mutedColor}>Taxa</Text>
                <Text fontSize="sm" fontWeight="bold">
                  {member.stats.approvalRate}%
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* View Details Button */}
          <Button
            size="sm"
            colorScheme="purple"
            variant="outline"
            leftIcon={<MdVisibility />}
            onClick={() => handleViewMember(member)}
          >
            Ver Detalhes
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  const teamMembers = teamData?.teamMembers || [];
  const teamTotals = teamData?.teamTotals || {};

  return (
    <Box>
      <Heading size="lg" mb={6} color={textColor}>
        Equipe Jurídica
      </Heading>

      {/* Team Summary */}
      <Card bg={cardBg} shadow="sm" mb={6}>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <Stat>
              <StatLabel>Total de Membros</StatLabel>
              <StatNumber>{teamMembers.length}</StatNumber>
              <StatHelpText>Ativos</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Total de Análises</StatLabel>
              <StatNumber>{teamTotals.totalAssignments}</StatNumber>
              <StatHelpText>{teamTotals.completedAssignments} concluídas</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Taxa de Conclusão</StatLabel>
              <StatNumber>{teamTotals.teamCompletionRate}%</StatNumber>
              <StatHelpText>Média da equipe</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Taxa de Aprovação</StatLabel>
              <StatNumber>{teamTotals.teamApprovalRate}%</StatNumber>
              <StatHelpText>Documentos aprovados</StatHelpText>
            </Stat>
          </Grid>
        </CardBody>
      </Card>

      {/* Team Members Grid */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
        {teamMembers.map((member) => (
          <GridItem key={member.member._id}>
            <MemberCard member={member} />
          </GridItem>
        ))}
      </Grid>

      {/* Member Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedMember && (
              <HStack>
                <Avatar name={selectedMember.member.name} bg="purple.500" />
                <Box>
                  <Text>{selectedMember.member.name}</Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {selectedMember.member.email}
                  </Text>
                </Box>
              </HStack>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingDetails ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : memberDetails && (
              <VStack align="stretch" spacing={6}>
                {/* Assignment Summary */}
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                  {['pendente', 'em_analise', 'aprovado', 'reprovado'].map((status) => (
                    <Card key={status} bg={cardBg} shadow="sm">
                      <CardBody>
                        <Text fontSize="sm" color={mutedColor} mb={1}>
                          {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold">
                          {memberDetails.assignments[status]?.length || 0}
                        </Text>
                        <Badge colorScheme={getStatusColor(status)}>
                          {status}
                        </Badge>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>

                {/* Assignments Table */}
                <Card bg={cardBg} shadow="sm">
                  <CardHeader>
                    <Heading size="md">Precatórios Designados</Heading>
                  </CardHeader>
                  <CardBody>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Número</Th>
                            <Th>Estado</Th>
                            <Th>Beneficiário</Th>
                            <Th>Valor</Th>
                            <Th>Status</Th>
                            <Th>Progresso</Th>
                            <Th>Ações</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {Object.values(memberDetails.assignments).flat().map((assignment) => (
                            <Tr key={assignment._id}>
                              <Td>{assignment.numero_precatorio}</Td>
                              <Td>
                                <Badge>{assignment.estado}</Badge>
                              </Td>
                              <Td>{assignment.nome_beneficiario}</Td>
                              <Td>{formatCurrency(assignment.valor_atualizado)}</Td>
                              <Td>
                                <Badge colorScheme={getStatusColor(assignment.status)}>
                                  {assignment.status}
                                </Badge>
                              </Td>
                              <Td>
                                <Box>
                                  <Text fontSize="xs">
                                    {assignment.analysisProgress?.percentComplete || 0}%
                                  </Text>
                                  <Progress
                                    value={assignment.analysisProgress?.percentComplete || 0}
                                    size="xs"
                                    colorScheme="blue"
                                  />
                                </Box>
                              </Td>
                              <Td>
                                <Button
                                  size="xs"
                                  leftIcon={<MdSwapHoriz />}
                                  onClick={() => handleReassign(assignment)}
                                >
                                  Reatribuir
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </CardBody>
                </Card>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reassignment Modal */}
      <Modal isOpen={isReassignOpen} onClose={onReassignClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reatribuir Precatório</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold">Precatório:</Text>
                <Text>{selectedAssignment?.numero_precatorio}</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={2}>Novo Responsável:</Text>
                <Select
                  placeholder="Selecione um membro"
                  value={newJuridicoId}
                  onChange={(e) => setNewJuridicoId(e.target.value)}
                >
                  {teamMembers
                    .filter(m => m.member._id !== selectedMember?.member._id)
                    .map((member) => (
                      <option key={member.member._id} value={member.member._id}>
                        {member.member.name} ({member.stats.totalAssigned} designados)
                      </option>
                    ))}
                </Select>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReassignClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              onClick={submitReassignment}
              isLoading={reassigning}
            >
              Confirmar Reatribuição
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TeamOverview;